const FAQ_SECTION_SELECTOR = "[data-ek-product-faq]";
const pendingFetches = new Map<string, AbortController>();
// sectionId → "productUrl:variantId" last requested, so repeated productVariantUpdated events don't refetch
const requestedKeys = new Map<string, string>();
const faqOpenGuards = new WeakMap<HTMLElement, MutationObserver>();

function closeAllFaqDetails(root: ParentNode): void {
  for (const detail of Array.from(root.querySelectorAll<HTMLDetailsElement>("[data-ek-product-faq] details"))) {
    detail.open = false;
    detail.removeAttribute("open");
  }
}

function resetFaqSectionState(section: HTMLElement): void {
  delete section.dataset.faqInteractive;
  delete section.dataset.faqInteractionBound;
  closeAllFaqDetails(section);
  guardFaqDetailsUntilInteractive(section);
}

function guardFaqDetailsUntilInteractive(section: HTMLElement): void {
  const existingObserver = faqOpenGuards.get(section);
  existingObserver?.disconnect();

  const observer = new MutationObserver(() => {
    if (section.dataset.faqInteractive === "true") {
      observer.disconnect();
      faqOpenGuards.delete(section);
      return;
    }

    closeAllFaqDetails(section);
  });

  observer.observe(section, {
    attributes: true,
    attributeFilter: ["open"],
    subtree: true,
  });

  faqOpenGuards.set(section, observer);
}

function bindFaqAccordionInteraction(root: ParentNode): void {
  for (const section of Array.from(root.querySelectorAll<HTMLElement>(FAQ_SECTION_SELECTOR))) {
    if (section.dataset.faqInteractionBound === "true") continue;
    section.dataset.faqInteractionBound = "true";

    for (const summary of Array.from(section.querySelectorAll<HTMLElement>("details > summary"))) {
      summary.addEventListener("click", (event) => {
        if (section.dataset.faqInteractive === "true") return;

        event.preventDefault();
        section.dataset.faqInteractive = "true";

        const detail = summary.closest("details");
        closeAllFaqDetails(section);
        if (detail) detail.open = true;
      });
    }
  }
}

function replaceSectionInnerHtml(sectionId: string, sectionHtml: string): void {
  const wrapper = document.getElementById(`shopify-section-${sectionId}`);
  if (!wrapper) return;

  const doc = new DOMParser().parseFromString(sectionHtml, "text/html");
  const root = doc.body.firstElementChild;
  const innerHtml = root && root.id === `shopify-section-${sectionId}` ? root.innerHTML : sectionHtml;
  wrapper.innerHTML = innerHtml;

  const section = wrapper.querySelector<HTMLElement>(FAQ_SECTION_SELECTOR);
  if (section) resetFaqSectionState(section);
  bindFaqAccordionInteraction(wrapper);
  wrapper.dispatchEvent(new CustomEvent("shopify:section:load", { bubbles: true }));
}

async function refreshProductFaqSection(sectionRoot: HTMLElement, variantId: number): Promise<void> {
  const sectionId = sectionRoot.dataset.sectionId;
  const productUrl = sectionRoot.dataset.productUrl;
  if (!sectionId || !productUrl) return;

  const requestKey = `${productUrl}:${variantId}`;
  if (requestedKeys.get(sectionId) === requestKey) return;
  requestedKeys.set(sectionId, requestKey);

  const fetchKey = `${sectionId}:${variantId}`;
  const existingController = pendingFetches.get(fetchKey);
  existingController?.abort();

  const controller = new AbortController();
  pendingFetches.set(fetchKey, controller);

  try {
    const response = await fetch(`${productUrl}?variant=${variantId}&sections=${sectionId}`, {
      signal: controller.signal,
    });
    if (!response.ok) {
      requestedKeys.delete(sectionId);
      return;
    }

    const data = (await response.json()) as Record<string, string | undefined>;
    const sectionHtml = data[sectionId];
    if (!sectionHtml) return;

    replaceSectionInnerHtml(sectionId, sectionHtml);
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return;
    requestedKeys.delete(sectionId);
    console.error("Failed to fetch product FAQ section:", error);
  } finally {
    if (pendingFetches.get(fetchKey) === controller) {
      pendingFetches.delete(fetchKey);
    }
  }
}

function handleVariantUpdated(event: Event): void {
  const detail = (event as CustomEvent<{ variantId?: number }>).detail;
  const variantId = detail?.variantId;
  if (!variantId) return;

  for (const sectionRoot of Array.from(document.querySelectorAll<HTMLElement>(FAQ_SECTION_SELECTOR))) {
    void refreshProductFaqSection(sectionRoot, variantId);
  }
}

let variantListenerInstalled = false;
let pageshowListenerInstalled = false;

function installVariantListener(): void {
  if (variantListenerInstalled) return;
  variantListenerInstalled = true;
  document.addEventListener("productVariantUpdated", handleVariantUpdated);
}

function installPageshowListener(): void {
  if (pageshowListenerInstalled) return;
  pageshowListenerInstalled = true;

  window.addEventListener("pageshow", () => {
    for (const section of Array.from(document.querySelectorAll<HTMLElement>(FAQ_SECTION_SELECTOR))) {
      resetFaqSectionState(section);
    }
  });
}

export function initEkProductFaq(root: ParentNode = document): void {
  installVariantListener();
  installPageshowListener();

  for (const section of Array.from(root.querySelectorAll<HTMLElement>(FAQ_SECTION_SELECTOR))) {
    resetFaqSectionState(section);
  }

  bindFaqAccordionInteraction(root);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initEkProductFaq());
} else {
  initEkProductFaq();
}

document.addEventListener("shopify:section:load", (event) => {
  const target = (event.target as ParentNode) || document;
  initEkProductFaq(target);
});
